import { addDays, format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import {
  DEFAULT_SCHEDULE_TIME_ZONE,
  US_EASTERN_DISPLAY_SUFFIX,
} from '@/lib/schedule-timezone';

const TZ = DEFAULT_SCHEDULE_TIME_ZONE;
const ZONE_SUFFIX = US_EASTERN_DISPLAY_SUFFIX;

function parseTicketInstant(iso: string | undefined | null): Date | null {
  if (iso == null || iso === '') return null;
  const parsed = parseISO(iso);
  if (isValid(parsed)) return parsed;
  const fallback = new Date(iso);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function easternDateKey(d: Date): string {
  return formatInTimeZone(d, TZ, 'yyyy-MM-dd');
}

function formatTimeInEastern(d: Date): string {
  return `${formatInTimeZone(d, TZ, 'h:mm a')}${ZONE_SUFFIX}`;
}

/** Chat bubbles: `Today · 6:02 AM EST`, `Yesterday · …`, `Jun 12 · 6:02 AM EST` */
export function formatTicketMessageTime(iso: string): string {
  const d = parseTicketInstant(iso);
  if (!d) return '';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const time = formatTimeInEastern(d);

  if (diffMs >= 0 && diffMs < 60_000) return 'Just now';

  const todayKey = easternDateKey(now);
  const msgKey = easternDateKey(d);
  if (msgKey === todayKey) return `Today · ${time}`;

  const nowEt = toZonedTime(now, TZ);
  const yesterdayKey = format(addDays(nowEt, -1), 'yyyy-MM-dd');
  if (msgKey === yesterdayKey) return `Yesterday · ${time}`;

  const datePart = formatInTimeZone(d, TZ, 'MMM d');
  return `${datePart} · ${time}`;
}

/** Inbox / list activity: `Just now`, `5 min`, `1 hr`, then `Jun 12` in Eastern */
export function formatTicketInboxRelativeTime(iso?: string): string {
  if (!iso) return '';
  const d = parseTicketInstant(iso);
  if (!d) return '';

  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'Just now';

  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs === 1 ? '1 hr' : `${hrs} hr`;

  const days = Math.floor(hrs / 24);
  if (days < 7) return days === 1 ? '1 day' : `${days} days`;

  return formatInTimeZone(d, TZ, 'MMM d');
}

/** Table / legacy list fallback after relative window */
export function formatTicketListTimeAgo(iso: string): string {
  const d = parseTicketInstant(iso);
  if (!d) return '';

  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;

  return formatInTimeZone(d, TZ, 'MMM d, yyyy');
}

/** Detail panels: `May 21, 2026` */
export function formatTicketDateShort(iso: string | undefined | null): string {
  const d = parseTicketInstant(iso);
  if (!d) return '—';
  return formatInTimeZone(d, TZ, 'MMM d, yyyy');
}

/** Full stamp: `Jun 12, 2026 6:02 AM EST` */
export function formatTicketDateTime(iso: string | undefined | null): string {
  const d = parseTicketInstant(iso);
  if (!d) return '—';
  return `${formatInTimeZone(d, TZ, 'MMM d, yyyy h:mm a')}${ZONE_SUFFIX}`;
}
